// components/ui/PageHeader.jsx
import React from "react";
import clsx from "clsx";

/**
 * PageHeader
 * Props:
 * - title: string | node
 * - subtitle?: string | node
 * - logoSrc: string (image utilisée pour le motif de fond)
 * - align?: "center" | "left"    (alignement du contenu)
 * - fullBleed?: boolean          (plein écran horizontal)
 * - height?: "sm" | "md" | "lg"  (hauteur du bandeau)
 * - rounded?: boolean            (coins arrondis)
 * - gradient?: { from, via, to } (couleurs du dégradé)
 * - patternSize?: string         (taille du motif, ex: "32px 32px")
 * - patternOpacity?: number      (0–100 en pourcentage, ex: 15)
 * - className?: string           (classes supplémentaires sur le conteneur externe)
 * - children?: node              (slot pour actions, breadcrumb, etc.)
 */
export default function PageHeader({
  title,
  subtitle,
  logoSrc,
  align = "center",
  fullBleed = true,
  height = "md",
  rounded = true,
  gradient = {
    from: "#E9FBFA",
    via: "#FFF9E6",
    to: "#FFE9EC",
  },
  patternSize = "32px 32px",
  patternOpacity = 15,
  className,
  children,
}) {
  const alignClasses =
    align === "left" ? "items-start text-left" : "items-center text-center";

  const heightClasses =
    height === "sm" ? "py-10" : height === "lg" ? "py-24" : "py-20"; // md

  // Conteneur largeur (full-bleed vs container)
  const Outer = ({ children }) =>
    fullBleed ? (
      <div className="relative left-1/2 -translate-x-1/2 w-[100vw] ">
        {children}
      </div>
    ) : (
      <div className="container mx-auto px-6">{children}</div>
    );

  return (
    <Outer>
      <div
        className={clsx(
          "relative md:mb-10 overflow-hidden border-t border-[#B35C38]/5",
          rounded && "rounded-2xl"
        )}
        // Dégradé de fond
        style={{
          backgroundImage: `linear-gradient(to bottom right, ${gradient.from}, ${gradient.via}, ${gradient.to}66)`,
        }}
      >
        {/* Motif logo répété */}
        {logoSrc && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              opacity: patternOpacity / 100,
              filter: "blur(1px)",
              backgroundImage: `url(${logoSrc})`,
              backgroundRepeat: "repeat",
              backgroundSize: patternSize,
            }}
            aria-hidden
          />
        )}

        {/* Contenu */}
        <div
          className={clsx(
            "relative z-10 flex flex-col justify-center px-6 text-[#3a3a3a]",
            heightClasses,
            alignClasses
          )}
        >
          {typeof title === "string" ? (
            <h2 className="text-3xl md:text-4xl font-bold mb-8">{title}</h2>
          ) : (
            title
          )}

          {subtitle &&
            (typeof subtitle === "string" ? (
              <p className="text-gray-600 md:text-lg max-w-3xl">{subtitle}</p>
            ) : (
              subtitle
            ))}

          {/* Slot (ex: boutons d’action, breadcrumb) */}
          {children && <div className="mt-4">{children}</div>}
        </div>
      </div>
    </Outer>
  );
}
